import { Component, computed, effect, inject, input, output, Signal, signal } from '@angular/core';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import { FilterService } from '../../../services/filter.service';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { MinutesFormat } from '@cumlaude/shared-utils';
import { SidebarService, TooltipDirective } from '@cumlaude/shared-components-overlays';
import { TableComponent } from '../../../shared/components/table/table/table.component';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { getLeafA, Level, sortByMeasure, unnest } from '../../../services/data-tree';
import {
	AlternatingMode,
	CellDataType,
	ColumnDef,
	createColumnDef,
	createDefaultFooterCellDef,
	createModel,
	TableModel,
} from '../../../shared/components/table/table/table.model';
import {
	createMeasureColumnWithExtraFooter,
	DataRow,
	ExportColumnDef,
	TOTAAL_LABEL,
} from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { att, percOfTotal } from '../../../services/measures';
import { MaandDropdownState } from '../maand-dropdown-state';
import { combineLatestWith, delay, filter, finalize, map, of, switchMap } from 'rxjs';
import { CssSpinnerComponent } from '../../../layout/css-spinner/css-spinner.component';
import { AsyncPipe } from '@angular/common';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import {
	AanwezigheidMeasure,
	BasicFilterExpression,
	CompoundFilterExpression,
	DataService,
	ExportFilter,
	FilterExpression,
} from '../../../services/data.service';
import { AggregatorConfig } from '../../../shared/dashboard/base-dashboard/aggregator-config';
import { noAgg0, noAggFiltered0 } from '../../../services/aggregation';
import { createAggFunctions } from '../../../shared/dashboard/base-dashboard/base-dashboard.component';
import { Attributes, createSelection, getLevelFilters } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DataRequestContext } from '../../../shared/dashboard/base-dashboard/dashboard-context';
import { SchoolaanwezigheidLeerlingSidebarComponent } from '../../../sidebars/schoolaanwezigheid/schoolaanwezigheid-leerling-sidebar.component';
import { last } from 'lodash-es';
import { UserService } from '../../../services/user.service';

export interface RedenI extends Attributes {
	aw_m_nr_leerlingen: number;
	aw_m_nr_lesdagen: number;
	aw_m_nr_lesuren: number;
	aw_m_nr_minuten_afwezig: number;
	aw_m_nr_minuten_aanwezig: number;
	xa: any;
}
export interface RedenA extends Attributes {
	aw_m_nr_leerlingen: number;
	aw_m_nr_lesdagen: number;
	aw_m_nr_lesuren: number;
	aw_m_nr_minuten_afwezig: number;
	aw_m_nr_minuten_aanwezig: number;
	aw_m_nr_leerlingenFiltered: number;
	aw_m_nr_lesdagenFiltered: number;
	aw_m_nr_lesurenFiltered: number;
	aw_m_nr_minuten_afwezigFiltered: number;
	aw_m_nr_minuten_aanwezigFiltered: number;
}

@Component({
	selector: 'app-aanwezigheid-redenen',
	templateUrl: './aanwezigheid-redenen.component.html',
	styleUrls: ['./aanwezigheid-redenen.component.scss'],
	imports: [IconComponent, SingleSelectDropdownComponent, TooltipDirective, TableComponent, CssSpinnerComponent, AsyncPipe],
})
export class AanwezigheidRedenenComponent implements ExportablePanel {
	private readonly qp = inject(QueryParamStateService);
	private readonly filterService = inject(FilterService);
	private readonly dataService = inject(DataService);
	private readonly sidebarService = inject(SidebarService);
	private readonly userService = inject(UserService);

	readonly filterExpressions = input.required<FilterExpression[]>();
	readonly permanentFilters = input.required<FilterExpression[]>();
	readonly leerlingDetails = input.required<boolean>();

	readonly loading = signal(false);
	loadingChanged = output<boolean>();

	constructor() {
		effect(() => {
			this.loadingChanged.emit(this.loading());
		});
	}

	readonly schooljaar = toSignal<string>(this.filterService.observeAsInput('aw_nm_schooljaar'));

	private readonly redenenAggConfig: AggregatorConfig<RedenI, RedenA> = {
		getSingleAggregators: () => ({
			aw_m_nr_leerlingen: noAgg0('aw_m_nr_leerlingen'),
			aw_m_nr_lesdagen: noAgg0('aw_m_nr_lesdagen'),
			aw_m_nr_lesuren: noAgg0('aw_m_nr_lesuren'),
			aw_m_nr_minuten_afwezig: noAgg0('aw_m_nr_minuten_afwezig'),
			aw_m_nr_minuten_aanwezig: noAgg0('aw_m_nr_minuten_aanwezig'),
			aw_m_nr_leerlingenFiltered: noAggFiltered0('aw_m_nr_leerlingen'),
			aw_m_nr_lesdagenFiltered: noAggFiltered0('aw_m_nr_lesdagen'),
			aw_m_nr_lesurenFiltered: noAggFiltered0('aw_m_nr_lesuren'),
			aw_m_nr_minuten_afwezigFiltered: noAggFiltered0('aw_m_nr_minuten_afwezig'),
			aw_m_nr_minuten_aanwezigFiltered: noAggFiltered0('aw_m_nr_minuten_aanwezig'),
		}),
		getMultiAggregators: () => [],
	};

	protected maandDropdownState = new MaandDropdownState('reden-maand');

	private readonly redenen = toSignal(
		toObservable(this.filterExpressions).pipe(
			filter(Boolean),
			combineLatestWith(toObservable(this.maandDropdownState.maandMetFallback), toObservable(this.permanentFilters)),
			switchMap(([filters, maand, permanentFilters]) => {
				this.loading.set(true);
				const maandFilter = maand > 0 ? [new BasicFilterExpression(['aw_nr_maand'], maand)] : [];
				return this.dataService
					.getAanwezigheidData({
						g: [['aw_fun_nm_abs_reden']],
						f: new CompoundFilterExpression([...filters, ...permanentFilters, ...maandFilter]),
						having: new BasicFilterExpression(['aw_fun_nm_abs_reden'], null, '<>'),
						r: [0, 1],
						m: [
							AanwezigheidMeasure.LESUREN,
							AanwezigheidMeasure.LESTIJD,
							this.leerlingDetails() ? AanwezigheidMeasure.LESDAGEN : AanwezigheidMeasure.LEERLINGEN,
						],
					})
					.pipe(finalize(() => this.loading.set(false)));
			}),
			map((resp): Level<RedenA, number[]> | undefined => {
				const { aggInit, aggCombine } = createAggFunctions(this.redenenAggConfig, [], resp.measures);
				const records = unnest<RedenA, number[]>(resp.data, undefined, undefined, aggInit, aggCombine);
				return records[0]?.[0];
			})
		),
		{ initialValue: undefined as Level<RedenA, number[]> | undefined }
	);

	protected sortOrder = this.qp.signal('sortOrder', { active: 'Gemiste tijd', direction: 'desc' });

	protected readonly redenenModel: Signal<TableModel<DataRow<RedenA>> | undefined> = computed(() => {
		const redenen = this.redenen();
		if (!redenen?.r) return undefined;

		switch (this.sortOrder().active) {
			case 'Reden':
				sortByMeasure(redenen, (r) => r[1].k ?? '', this.sortOrder().direction || 'asc');
				break;
			case 'Lesuren':
				sortByMeasure(redenen, (path) => getLeafA(path).aw_m_nr_lesuren ?? 0, this.sortOrder().direction || 'desc');
				break;
			default:
				sortByMeasure(redenen, (path) => getLeafA(path).aw_m_nr_minuten_afwezig, this.sortOrder().direction || 'desc');
				break;
		}

		const additionalLeerlingDetails = <ColumnDef<DataRow<RedenA>>[]>[
			createMeasureColumnWithExtraFooter(
				'Percentage',
				percOfTotal('aw_m_nr_lesuren', 'aw_m_nr_lesuren'),
				percOfTotal('aw_m_nr_lesurenFiltered', 'aw_m_nr_lesuren'),
				{
					dataType: 'percentage',
					format: '1.1-1',
				}
			),
			createMeasureColumnWithExtraFooter('Lesdagen', att('aw_m_nr_lesdagen'), att('aw_m_nr_lesdagenFiltered')),
		];

		const additionalStatistieken = <ColumnDef<DataRow<RedenA>>[]>[
			createMeasureColumnWithExtraFooter('Leerlingen', att('aw_m_nr_leerlingen'), att('aw_m_nr_leerlingenFiltered')),
		];

		const gemisteTijd = createMeasureColumnWithExtraFooter<RedenI, RedenA>(
			'Gemiste tijd',
			att('aw_m_nr_minuten_afwezig'),
			att('aw_m_nr_minuten_afwezigFiltered'),
			{
				dataType: 'minutes',
				format: 'hu mm',
			}
		);
		// De laatste footerrij toont het geroosterde totaal; gemiste tijd is daar niet van toepassing.
		gemisteTijd.footer = { ...createDefaultFooterCellDef(), getValue: () => '' };

		return {
			...createModel(redenen.r.map((_path, ix) => ({ _path, _id: ix.toString() })) as DataRow<RedenA>[], (row) => row._id),
			showFooters: this.leerlingDetails(),
			stickyFooters: false,
			showExtraFooters: true,
			rowsClickable: !this.leerlingDetails() && !this.userService.isBestuur(),
			columnDefs: [
				{
					...createColumnDef<DataRow<RedenA>>('Reden', 'Reden', false, (r) => r._path[1].k ?? ''),
					footer: { ...createDefaultFooterCellDef(), getValue: () => TOTAAL_LABEL + ' geroosterd' },
					extraFooter: { ...createDefaultFooterCellDef(), getValue: (_) => TOTAAL_LABEL + ' afwezig' },
					sortable: true,
					sortActive: this.sortOrder().active === 'Reden',
					sortDirection: this.sortOrder().direction,
				},
				...(this.leerlingDetails() ? additionalLeerlingDetails : additionalStatistieken),
				createMeasureColumnWithExtraFooter('Lesuren', att('aw_m_nr_lesuren'), att('aw_m_nr_lesurenFiltered')),
				gemisteTijd,
			],
			alternating: AlternatingMode.COLOR,
			getRowgroup: (rowModel: DataRow<RedenA>) => ({ groupId: Number(rowModel._id), lastOfGroup: false }),
		};
	});

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		const maandLabel = this.maandDropdownState.selectedOptieLabel();

		const model = this.redenenModel();

		let columns: ExportColumnDef[] = [];
		let rows: any[] = [];

		if (model && model.data.length > 0) {
			columns = model.columnDefs.map((col) => ({
				name: col.header.getValue(model) || col.column,
				type: <CellDataType>col.body.dataType,
				format: <MinutesFormat>col.body.format,
			}));
			rows = [
				...model.data.map((row) => model.columnDefs.map((col) => col.body.getValue(row))),
				...(model.showExtraFooters ? [model.columnDefs.map((col) => col.extraFooter?.getValue?.(model))] : []),
				...(model.showFooters ? [model.columnDefs.map((col) => col.footer?.getValue?.(model))] : []),
			];
		}

		return {
			name: 'Afwezigheidsredenen',
			...(exportOptions.showTitle ? { title: 'Afwezigheidsredenen' } : {}),
			...(exportOptions.showFilters ? { filters, displayOptions: [{ label: 'Maand', value: maandLabel }] } : {}),
			data: [
				{
					columns,
					rows,
				},
			],
		};
	}

	spinnerDelay = of(true).pipe(delay(200));

	protected rowClicked(clickedRow: DataRow<RedenA>) {
		if (this.userService.isBestuur() || this.leerlingDetails()) return undefined;

		const dataRoot = this.redenen();
		if (!dataRoot) return undefined;

		const maand = this.maandDropdownState.maandMetFallback();
		const maandFilter = maand > 0 ? [new BasicFilterExpression(['aw_nr_maand'], maand)] : [];

		const filter = new CompoundFilterExpression([...this.filterExpressions(), ...this.permanentFilters(), ...maandFilter]);

		const context: DataRequestContext<RedenA> = {
			dataRoot,
			filter,
			groupNames: ['aw_fun_nm_abs_reden'],
			subgroupNames: [],
			measureNames: [],
		};

		const selection = createSelection(context, clickedRow._path, {
			detailUrl: '/details/leerling/aanwezigheid',
			mergeQueryParams: false,
			provider: 'aanwezigheid',
			getSize: att('aw_m_nr_leerlingen'),
			extraFilters: [],
		});

		const schooljaar = this.schooljaar();
		this.sidebarService.open(SchoolaanwezigheidLeerlingSidebarComponent, {
			data: {
				reden: last(clickedRow._path)!.k!,
				maandJaar: this.maandDropdownState.getMaandMetJaar(schooljaar),
				maand,
				schooljaar,
				selection,
				filter: new CompoundFilterExpression([...filter.filters, ...getLevelFilters(context, clickedRow._path)]),
			},
		});
	}
}
